import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { NotFoundContent } from "@/pages/NotFound";

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    if (article) {
      window.scrollTo({ top: 0 });
    }
  }, [article]);

  if (!article) {
    return <NotFoundContent />;
  }

  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    datePublished: `${article.date}T09:00:00+08:00`,
    articleSection: article.category,
    inLanguage: "zh-CN",
    description: article.excerpt,
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <section className="pt-28 md:pt-36 pb-6 border-b border-background-200/70">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <Breadcrumb items={[{ label: "文章专题", to: "/article" }, { label: article.title }]} />
          <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] text-foreground-600">
            <span className="px-2 py-0.5 rounded-full bg-primary-100/60 text-primary-500 font-bold">{article.category}</span>
            <span>{article.date}</span>
            <span>· {article.readTime} 阅读</span>
          </div>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl leading-snug text-foreground-950">{article.title}</h1>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{article.excerpt}</p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 md:px-6 py-8">
        <div className="rounded-xl overflow-hidden border border-background-200/70 aspect-[16/9]">
          <img src={article.cover} alt={article.title} title={article.title} className="w-full h-full object-cover object-top" />
        </div>
        <div className="mt-8 space-y-6">
          {article.content.map((p, i) => (
            <p key={i} className="text-[15px] leading-8 text-foreground-700">
              {i === 0 ? (
                <>
                  <span className="float-left mr-3 text-5xl font-heading leading-none text-primary-500">{p.charAt(0)}</span>
                  {p.slice(1)}
                </>
              ) : (
                p
              )}
            </p>
          ))}
        </div>

        <div className="mt-10 rounded-xl bg-background-100 border border-background-200/70 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-sm text-foreground-700">
            看完这篇还意犹未尽？去 <Link to="/tuijian" className="text-primary-500 font-bold hover:text-primary-600">新作推荐</Link> 看看大家都在追哪部韩漫。
          </p>
          <Link
            to="/tuijian"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 font-bold text-xs whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            <i className="ri-stack-line"></i> 看热门连载
          </Link>
        </div>
      </article>

      <section className="border-t border-background-200/70 bg-background-100">
        <div className="mx-auto max-w-3xl px-4 md:px-6 py-10">
          <h2 className="font-heading text-xl text-foreground-950 mb-5">相关阅读</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group rounded-lg overflow-hidden bg-background-200/60 border border-background-300/50 hover:border-primary-400/60 transition-colors"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={a.cover} alt={a.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <p className="text-xs font-heading text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">{a.title}</p>
                  <p className="mt-1.5 text-[10px] text-foreground-600">{a.category} · {a.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}